/**
 * Block Types and Set Categories for Program Builder
 *
 * Block Types determine how exercises are grouped:
 * - single: One exercise per block
 * - superset/antagonist_superset: Two exercises alternating
 * - tri_set: Three exercises in sequence
 * - giant_set: Four or more exercises
 * - drop_set: Single exercise with weight drops
 * - staggered: Two exercises with alternating pattern
 */

import { getDefaultPrescription } from '../utils/exerciseDefaults';
import { colors as theme } from '../theme';

// Block type definitions
export const BLOCK_TYPES = {
  single: {
    id: 'single',
    label: 'Single Exercise',
    description: 'Standard single exercise or multiple exercises performed sequentially',
    minExercises: 1,
    maxExercises: 10,
    icon: '1️⃣',
  },
  superset: {
    id: 'superset',
    label: 'Superset',
    description: 'Two exercises performed back-to-back',
    minExercises: 2,
    maxExercises: 2,
    icon: '🔄',
  },
  antagonist_superset: {
    id: 'antagonist_superset',
    label: 'Antagonist Superset',
    description: 'Two opposing muscle group exercises',
    minExercises: 2,
    maxExercises: 2,
    icon: '↔️',
  },
  tri_set: {
    id: 'tri_set',
    label: 'Tri-Set',
    description: 'Three exercises performed in sequence',
    minExercises: 3,
    maxExercises: 3,
    icon: '🔺',
  },
  giant_set: {
    id: 'giant_set',
    label: 'Giant Set',
    description: 'Four or more exercises in sequence',
    minExercises: 4,
    maxExercises: 6,
    icon: '💪',
  },
  drop_set: {
    id: 'drop_set',
    label: 'Drop Set',
    description: 'Single exercise with progressive weight drops',
    minExercises: 1,
    maxExercises: 1,
    icon: '📉',
  },
  staggered: {
    id: 'staggered',
    label: 'Staggered Set',
    description: 'Two exercises with alternating pattern',
    minExercises: 2,
    maxExercises: 2,
    icon: '⚡',
  },
};

// Set category definitions with configuration parameters
export const SET_CATEGORIES = {
  normal: {
    id: 'normal',
    label: 'Normal',
    description: 'Standard sets with rest between',
    clientDescription: 'Complete all sets of each exercise with rest between sets before moving to the next exercise.',
    blockTypes: ['single'],
    params: [],
    defaultParams: {},
    icon: '1️⃣',
  },
  drop_set: {
    id: 'drop_set',
    label: 'Drop Set',
    description: 'Reduce weight and continue without rest',
    clientDescription: 'Reduce weight immediately and continue without rest. Drop 20-30% each time.',
    blockTypes: ['drop_set'],
    params: [
      { key: 'dropsCount', label: 'Number of Drops', type: 'number', default: 2, min: 1, max: 5 },
      { key: 'dropPercentEach', label: 'Drop % Each', type: 'number', default: 20, min: 10, max: 40 },
      { key: 'repsPerDrop', label: 'Reps Per Drop', type: 'text', default: 'AMRAP' },
    ],
    defaultParams: { dropsCount: 2, dropPercentEach: 20, repsPerDrop: 'AMRAP' },
    icon: '📉',
  },
  superset: {
    id: 'superset',
    label: 'Superset',
    description: 'Alternate between two exercises',
    clientDescription: 'Two exercises performed back-to-back with no rest. Rest only after both exercises.',
    blockTypes: ['superset'],
    params: [
      { key: 'restAfterPairSeconds', label: 'Rest After Pair (s)', type: 'number', default: 90, min: 30, max: 180 },
    ],
    defaultParams: { restAfterPairSeconds: 90 },
    icon: '🔄',
  },
  antagonist_superset: {
    id: 'antagonist_superset',
    label: 'Antagonist Superset',
    description: 'Opposing muscle groups back-to-back',
    clientDescription: 'Opposing muscle groups back-to-back (e.g., biceps then triceps). Rest only after both.',
    blockTypes: ['antagonist_superset'],
    params: [
      { key: 'restAfterPairSeconds', label: 'Rest After Pair (s)', type: 'number', default: 90, min: 30, max: 180 },
    ],
    defaultParams: { restAfterPairSeconds: 90 },
    icon: '↔️',
  },
  negative: {
    id: 'negative',
    label: 'Negative/Eccentric',
    description: 'Emphasis on slow eccentric phase',
    clientDescription: 'Slow, controlled lowering phase (4+ seconds). Focus on the eccentric movement.',
    blockTypes: ['single'],
    params: [
      { key: 'eccentricSeconds', label: 'Eccentric Time (s)', type: 'number', default: 4, min: 2, max: 10 },
      { key: 'applyLastSetOnly', label: 'Last Set Only', type: 'boolean', default: false },
    ],
    defaultParams: { eccentricSeconds: 4, applyLastSetOnly: false },
    icon: '⬇️',
  },
  tri_set: {
    id: 'tri_set',
    label: 'Tri-Set',
    description: 'Three exercises performed consecutively',
    clientDescription: 'Three exercises performed consecutively. Rest only after all three are complete.',
    blockTypes: ['tri_set'],
    params: [
      { key: 'restAfterTriSeconds', label: 'Rest After Tri (s)', type: 'number', default: 120, min: 60, max: 240 },
    ],
    defaultParams: { restAfterTriSeconds: 120 },
    icon: '🔺',
  },
  giant_set: {
    id: 'giant_set',
    label: 'Giant Set',
    description: 'Four+ exercises performed consecutively',
    clientDescription: 'Four or more exercises in sequence for maximum metabolic stress. Rest after the full circuit.',
    blockTypes: ['giant_set'],
    params: [
      { key: 'restAfterGiantSeconds', label: 'Rest After Giant (s)', type: 'number', default: 150, min: 90, max: 300 },
    ],
    defaultParams: { restAfterGiantSeconds: 150 },
    icon: '💪',
  },
  staggered: {
    id: 'staggered',
    label: 'Staggered',
    description: 'Alternate between main and accessory exercise',
    clientDescription: 'Alternate between main and secondary exercise. Great for time efficiency.',
    blockTypes: ['staggered'],
    params: [
      { key: 'pattern', label: 'Pattern', type: 'select', default: 'alternate', options: [
        { value: 'alternate', label: 'Alternate (A-B-A-B)' },
        { value: 'interspersed', label: 'Interspersed (A-A-B-A-A-B)' },
      ]},
      { key: 'restBetweenSeconds', label: 'Rest Between (s)', type: 'number', default: 60, min: 30, max: 120 },
    ],
    defaultParams: { pattern: 'alternate', restBetweenSeconds: 60 },
    icon: '⚡',
  },
};

// Helper functions
export const getBlockTypeForCategory = (category) => {
  const cat = SET_CATEGORIES[category];
  return cat?.blockTypes?.[0] || 'single';
};

export const getCategoryForBlockType = (blockType) => {
  // Find the first category that supports this block type
  for (const [key, cat] of Object.entries(SET_CATEGORIES)) {
    if (cat.blockTypes.includes(blockType)) {
      return key;
    }
  }
  return 'normal';
};

export const getMinExercisesForCategory = (category) => {
  const blockType = getBlockTypeForCategory(category);
  return BLOCK_TYPES[blockType]?.minExercises || 1;
};

export const getMaxExercisesForCategory = (category) => {
  const blockType = getBlockTypeForCategory(category);
  return BLOCK_TYPES[blockType]?.maxExercises || 1;
};

export const getDefaultParamsForCategory = (category) => {
  return SET_CATEGORIES[category]?.defaultParams || {};
};

// Get client-facing description for a set category
export const getClientDescription = (categoryId) => {
  return SET_CATEGORIES[categoryId]?.clientDescription || 'Standard sets with rest between.';
};

// Position labels (A1, A2, B1, B2, etc.)
export const getPositionLabel = (blockIndex, position) => {
  const letter = String.fromCharCode(65 + blockIndex); // A, B, C...
  return `${letter}${position}`;
};

// Format category display with params
export const formatCategoryDisplay = (category, params = {}) => {
  const cat = SET_CATEGORIES[category];
  if (!cat) return 'Normal';

  let display = cat.label;

  if (category === 'drop_set' && params.dropsCount) {
    display += ` (${params.dropsCount} drops)`;
  } else if (category === 'negative' && params.eccentricSeconds) {
    display += ` (${params.eccentricSeconds}s eccentric)`;
  }

  return display;
};

// RIR (Reps in Reserve) options
export const RIR_OPTIONS = [
  { value: 0, label: '0 (Failure)', feeling: 'fail', color: theme.danger },
  { value: 1, label: '1 RIR', feeling: 'hard', color: theme.warning },
  { value: 2, label: '2 RIR', feeling: 'medium', color: '#FFCC00' },
  { value: 3, label: '3+ RIR', feeling: 'easy', color: theme.success },
];

export const getRIRFeeling = (rir) => {
  if (rir === 0) return 'fail';
  if (rir === 1) return 'hard';
  if (rir === 2) return 'medium';
  return 'easy';
};

export const getRIRColor = (rir) => {
  const option = RIR_OPTIONS.find(o => o.value === rir);
  return option?.color || theme.textMuted;
};

// Cardio/Stretch Intensity Levels (4 levels as requested)
export const CARDIO_INTENSITY_LEVELS = [
  {
    value: 'low',
    label: 'Low',
    shortLabel: 'Low',
    description: 'Easy pace, can hold conversation',
    color: theme.success,
    hrZone: '50-60% max HR',
  },
  {
    value: 'moderate',
    label: 'Moderate',
    shortLabel: 'Mod',
    description: 'Steady pace, slightly breathless',
    color: '#FFCC00',
    hrZone: '60-70% max HR',
  },
  {
    value: 'high',
    label: 'High',
    shortLabel: 'High',
    description: 'Hard pace, difficult to talk',
    color: theme.warning,
    hrZone: '70-85% max HR',
  },
  {
    value: 'maximum',
    label: 'Maximum',
    shortLabel: 'Max',
    description: 'All-out effort, unsustainable',
    color: theme.danger,
    hrZone: '85-100% max HR',
  },
];

// Get intensity level color
export const getIntensityColor = (intensity) => {
  const level = CARDIO_INTENSITY_LEVELS.find(l => l.value === intensity);
  return level?.color || theme.textMuted;
};

// Get intensity level label
export const getIntensityLabel = (intensity) => {
  const level = CARDIO_INTENSITY_LEVELS.find(l => l.value === intensity);
  return level?.label || 'Unknown';
};

// Format duration from seconds to readable format
export const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Parse duration string (MM:SS or just seconds) to total seconds
export const parseDuration = (input) => {
  if (!input) return 0;
  if (typeof input === 'number') return input;

  // Handle MM:SS format
  if (input.includes(':')) {
    const [mins, secs] = input.split(':').map(n => parseInt(n) || 0);
    return (mins * 60) + secs;
  }

  // Handle just seconds
  return parseInt(input) || 0;
};

// Default block structure
export const createDefaultBlock = (blockOrder = 0) => ({
  id: Date.now(),
  block_order: blockOrder,
  block_type: 'single',
  set_category: 'normal',
  category_params: {},
  rest_seconds: 60,
  notes: '',
  items: [],
});

// Create a block item
export const createBlockItem = (exercise, position = 1) => {
  const { sets, reps } = getDefaultPrescription(exercise);
  return {
    id: Date.now(),
    exercise_id: exercise.id,
    exercise_name: exercise.name,
    movement_category: exercise.movement_category || '',
    position,
    sets,
    reps,
    load_percentage: '',
    notes: '',
  };
};

const blockTypesExport = {
  BLOCK_TYPES,
  SET_CATEGORIES,
  RIR_OPTIONS,
  CARDIO_INTENSITY_LEVELS,
  getBlockTypeForCategory,
  getCategoryForBlockType,
  getMinExercisesForCategory,
  getMaxExercisesForCategory,
  getDefaultParamsForCategory,
  getClientDescription,
  getPositionLabel,
  formatCategoryDisplay,
  getRIRFeeling,
  getRIRColor,
  getIntensityColor,
  getIntensityLabel,
  formatDuration,
  parseDuration,
  createDefaultBlock,
  createBlockItem,
};
export default blockTypesExport;
